import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Paper, CircularProgress, Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { verificaSessioneCorso } from "../utils/otpClient";
import { useKpiCorsoData } from "../hooks/useKpiCorsoData";
import { calcolaKpiStudenti, calcolaKpiCorso, esportaKpiXlsx } from "../utils/kpiCorso";
import { TabKpi } from "./utility/kpiStudenti/CorsoDetail";

export default function ReportClienteCorso() {
  const { corsoId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [corso, setCorso] = useState(null);
  const [email, setEmail] = useState("");

  useEffect(() => {
    const token = sessionStorage.getItem(`clienteTokenCorso_${corsoId}`);
    if (!token) { navigate(`/accesso-corso/${corsoId}`); return; }

    verificaSessioneCorso(token, corsoId)
      .then((result) => {
        setEmail(result.email);
        return getDoc(doc(db, "corsi", corsoId));
      })
      .then((snap) => {
        if (snap.exists()) setCorso({ id: snap.id, ...snap.data() });
      })
      .catch(() => {
        sessionStorage.removeItem(`clienteTokenCorso_${corsoId}`);
        navigate(`/accesso-corso/${corsoId}`);
      })
      .finally(() => setLoading(false));
  }, [corsoId]);

  const { eventi, presenze, risultatiQuiz, loading: loadingDati } = useKpiCorsoData(corsoId, corso?.eventiIds);

  const kpiStudenti = useMemo(
    () => calcolaKpiStudenti(eventi, presenze, risultatiQuiz, corso?.iscritti || []),
    [eventi, presenze, risultatiQuiz, corso]
  );
  const kpiCorsoSerie = useMemo(
    () => calcolaKpiCorso(eventi, presenze, risultatiQuiz, corso?.iscritti || []),
    [eventi, presenze, risultatiQuiz, corso]
  );

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <svg width="120" viewBox="0 0 200 100" role="img">
          <defs>
            <linearGradient id="rccGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#E63329"/>
              <stop offset="50%" stopColor="#9B2DB5"/>
              <stop offset="100%" stopColor="#1A56A0"/>
            </linearGradient>
            <linearGradient id="rccBarGrad" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.4"/>
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0.95"/>
            </linearGradient>
          </defs>
          <rect x="4" y="4" width="60" height="60" rx="10" fill="url(#rccGrad)"/>
          <rect x="18" y="0" width="5" height="10" rx="2.5" fill="#ffffff" opacity="0.9"/>
          <rect x="44" y="0" width="5" height="10" rx="2.5" fill="#ffffff" opacity="0.9"/>
          <rect x="10" y="38" width="6" height="18" rx="2" fill="url(#rccBarGrad)"/>
          <rect x="20" y="30" width="6" height="26" rx="2" fill="url(#rccBarGrad)"/>
          <rect x="30" y="22" width="6" height="34" rx="2" fill="url(#rccBarGrad)"/>
          <rect x="40" y="28" width="6" height="28" rx="2" fill="url(#rccBarGrad)"/>
          <rect x="50" y="16" width="6" height="40" rx="2" fill="url(#rccBarGrad)"/>
          <polyline points="13,38 23,30 33,22 43,28 53,16"
            fill="none" stroke="#F59E0B" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="53" cy="16" r="2.5" fill="#F59E0B"/>
        </svg>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            Accesso come: <strong>{email}</strong>
          </Typography>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />}
            onClick={() => esportaKpiXlsx(corso, kpiStudenti)} disabled={kpiStudenti.length === 0}>
            Esporta report
          </Button>
        </Box>
      </Box>

      <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: "#f0f4ff" }}>
        <Typography variant="h5" fontWeight="bold" color="primary">
          {corso?.nome || "—"}
        </Typography>
        {corso?.descrizione && (
          <Typography variant="body2" color="text.secondary">{corso.descrizione}</Typography>
        )}
      </Paper>

      {loadingDati ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : eventi.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            I KPI di questo corso non sono ancora disponibili.
          </Typography>
        </Paper>
      ) : (
        <TabKpi kpiStudenti={kpiStudenti} kpiCorsoSerie={kpiCorsoSerie} />
      )}
    </Box>
  );
}
